import React, { useEffect, useState } from 'react';
import { FieldRenderProps } from 'react-final-form';
import { components } from 'react-select';
import { Image } from '@/shared/ui/image';
import { usePhoneMasks } from '@/entities/phone-mask/model/hooks';

import {
  OptionWrapper,
  Label,
  PhoneInputCode,
  PhoneInput,
  ImageControl,
  ErrorText,
  OptionCheck,
  Select,
  OptionCountry,
  SelectWrapper,
  PhoneFieldWrapper,
  OptionMenu,
  OptionMenuList,
  OptionMenuPortal,
  MainWrraper,
} from './styles';
import { SkeletonLoader } from '../../skeleton';
import { useUser } from '@/shared/providers/user-provider/use-user';

interface PhoneFormFieldProps extends FieldRenderProps<string> {
  label: string;
}

const Option = ({ children, ...props }) => {
  return (
    <components.Option {...props}>
      <OptionWrapper $isSelected={props.isSelected}>
        <ImageControl
          alt="country"
          width={20}
          height={20}
          src={props.data.imageUrl}
        />
        <OptionCountry>{props.data.country}</OptionCountry>
        {props.data.code}
        {props.isSelected && (
          <OptionCheck>
            <Image
              src={'/images/icons/check.svg'}
              width={16}
              height={16}
              alt="Checkmark"
            />
          </OptionCheck>
        )}
      </OptionWrapper>
    </components.Option>
  );
};

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <Image
        src="/images/icons/indicator-lang.svg"
        width={16}
        height={16}
        style={
          props.selectProps.menuIsOpen
            ? {
                transition: 'all .2s ease',
                transform: 'rotate(0deg)',
                marginLeft: '8px',
              }
            : {
                transition: 'all .2s ease',
                transform: 'rotate(180deg)',
                marginLeft: '8px',
              }
        }
        alt="indicator"
      />
    </components.DropdownIndicator>
  );
};

const Control = ({ children, ...props }) => {
  const currentOption = props.getValue()[0];
  return (
    <components.Control {...props}>
      {currentOption && (
        <ImageControl
          alt="country"
          width={24}
          height={24}
          src={currentOption.imageUrl}
        />
      )}
      {children}
    </components.Control>
  );
};

const Menu = ({ children, ...props }) => {
  return (
    <components.Menu {...props}>
      <OptionMenu>{children}</OptionMenu>
    </components.Menu>
  );
};

const MenuList = ({ children, ...props }) => {
  return (
    <components.MenuList {...props}>
      <OptionMenuList>{children}</OptionMenuList>
    </components.MenuList>
  );
};

const MenuPortal = ({ children, ...props }) => {
  return (
    <components.MenuPortal {...props}>
      <OptionMenuPortal>{children}</OptionMenuPortal>
    </components.MenuPortal>
  );
};

const PhoneField: React.FC<PhoneFormFieldProps> = ({
  input,
  meta,
  label,
  onCountryChange,
  country,
  ...rest
}) => {
  const {
    phoneMasks,
    isLoading: isPhoneMasksLoading,
    isError,
  } = usePhoneMasks();
  const [isFocused, setIsFocused] = useState(false);
  const [currentCountry, setCurrentCountry] = useState(null);
  const { user } = useUser();
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  useEffect(() => {
    if (user.phone && user.phone.code !== '' && !isPhoneMasksLoading) {
      const selectedCountry = phoneMasks.find(
        (mask) => mask.code === user.phone.code
      );
      setCurrentCountry(selectedCountry);
    } else if (!isPhoneMasksLoading && phoneMasks.length > 0) {
      const defaultCountry =
        phoneMasks.find((mask) => mask.default) || phoneMasks[0];
      setCurrentCountry(defaultCountry);
    }
  }, [user, isPhoneMasksLoading]);

  useEffect(() => {
    if (country) {
      setCurrentCountry(country);
    }
  }, [country]);

  if (isPhoneMasksLoading || !currentCountry) {
    return (
      <>
        {label && <Label>{label}</Label>}
        <PhoneFieldWrapper>
          <SkeletonLoader width={70} height={38} />
        </PhoneFieldWrapper>
      </>
    );
  }

  return (
    <MainWrraper>
      {label && <Label>{label}</Label>}
      <PhoneFieldWrapper
        $error={meta.error && meta.touched}
        $focused={isPhoneFocused}
        onBlur={() => setIsPhoneFocused(false)}
        onClick={() => setIsPhoneFocused(true)}
      >
        <div id="phone-input">
          <SelectWrapper>
            <Select
              $isFocused={isFocused}
              options={phoneMasks}
              value={currentCountry}
              unstyled
              delimiter={false}
              menuShouldScrollIntoView={false}
              isSearchable={false}
              isDisabled={rest?.disabled}
              components={{
                DropdownIndicator,
                Control,
                Option,
                Menu,
                MenuList,
                MenuPortal,
              }}
              getOptionLabel={(option) => `${option.country} ${option.code}`}
              getOptionValue={(option) => option.country}
              onFocus={() => setIsFocused(true)}
              isFocused={isFocused}
              onMenuClose={() => setIsFocused(false)}
              onMenuOpen={() => setIsFocused(true)}
              classNamePrefix={'phone-countries-select'}
              onChange={(country) => {
                setCurrentCountry(country);
                onCountryChange(country);
              }}
            />
            <PhoneInputCode>{currentCountry.code}</PhoneInputCode>
            <PhoneInput
              mask={currentCountry.mask}
              autoComplete="off"
              {...input}
              {...rest}
              value={input?.value?.replace(currentCountry?.code, '')}
              onChange={(e) => {
                input.onChange(e.target.value);
              }}
            />
          </SelectWrapper>
        </div>
      </PhoneFieldWrapper>
      {meta.error && meta.touched && <ErrorText>{meta.error}</ErrorText>}
    </MainWrraper>
  );
};

export { PhoneField };
